import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Check, Circle, Loader2, Rocket, X } from "lucide-react";

type Brief = { positioning?: string; target_audience?: string; key_messages?: string[]; next_actions?: string[] };
type Approval = { id: string; action: string; args_hash: string; status: string; preview: { before?: unknown; after?: unknown } | null; created_at: string };
type Draft = { id: string; name: string; price: number; category: string | null; image: string | null; description: string | null };
type Check = { key: string; ok: boolean; warning?: boolean; detail: string };
type Profile = { business_idea: string; target_audience: string; budget_range: string; experience_level: string; tone: string; approved_at: string | null } | null;
type SState = {
  store: { id: string; store_name: string | null; default_currency: string | null; is_production: boolean | null } | null;
  profile: Profile;
  brief_approval: Approval | null;
  brand_approval: Approval | null;
  drafts: Draft[];
  checks: Check[];
};

const CUR: Record<string, string> = { eur: "€", usd: "$", gbp: "£", cad: "CA$" };
const CHECK_LABELS: Record<string, string> = {
  payments: "Paiements connectés",
  currency: "Devise de la boutique",
  images: "Images des produits",
  policies: "Politiques de la boutique",
};
const BUDGETS = ["< 500", "500 – 2 000", "2 000 – 5 000", "> 5 000", "À définir"];
const LEVELS = ["débutant", "intermédiaire", "avancé"];
const TONES: { value: string; label: string }[] = [
  { value: "premium", label: "Premium" },
  { value: "chaleureux", label: "Chaleureux" },
  { value: "professionnel", label: "Professionnel" },
  { value: "dynamique", label: "Dynamique" },
  { value: "minimaliste", label: "Minimaliste" },
];

async function invoke<T>(name: string, body: unknown): Promise<T> {
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (error) {
    let msg = "Une erreur est survenue.";
    try { msg = (await (error as { context?: Response }).context?.json())?.error ?? msg; } catch { /* keep default */ }
    throw new Error(typeof msg === "string" ? msg : "Requête invalide.");
  }
  return data as T;
}

const STEP_TITLES = [
  "Votre business",
  "Brief de lancement",
  "Produits & identité (brouillons)",
  "Vérifications",
  "Lancement",
];

export function LaunchAgentTab() {
  const { toast } = useToast();
  const [stores, setStores] = useState<{ id: string; store_name: string | null }[]>([]);
  const [storeId, setStoreId] = useState<string>("");
  const [state, setState] = useState<SState | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [openStep, setOpenStep] = useState(0);

  // Step 1 form
  const [idea, setIdea] = useState("");
  const [audience, setAudience] = useState("");
  const [budget, setBudget] = useState("À définir");
  const [level, setLevel] = useState("débutant");
  const [tone, setTone] = useState("premium");
  const [questions, setQuestions] = useState<string[]>([]);
  const [answers, setAnswers] = useState<string[]>(["", ""]);

  // Step 2 form
  const [briefPos, setBriefPos] = useState("");
  const [briefAud, setBriefAud] = useState("");
  const [briefMsgs, setBriefMsgs] = useState("");
  const [briefActs, setBriefActs] = useState("");
  const [briefEdited, setBriefEdited] = useState(false);
  const [briefLoadedId, setBriefLoadedId] = useState<string | null>(null);

  const loadState = useCallback(async (id: string) => {
    try {
      const s = await invoke<SState>("launch-agent", { op: "state", store_id: id });
      setState(s);
      setOpenStep((prev) => {
        const done = [
          !!s.profile?.business_idea,
          !!s.profile?.approved_at,
          (s.drafts?.length ?? 0) > 0,
          (s.checks?.length ?? 0) > 0 && s.checks.every((c) => c.ok),
          !!s.store?.is_production,
        ];
        const active = done.findIndex((d) => !d);
        return prev === -1 || prev === 0 ? (active === -1 ? 4 : active) : prev;
      });
    } catch (e) {
      toast({ title: "Lancement IA indisponible", description: (e as Error).message, variant: "destructive" });
      setState(null);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data } = await supabase.from("store_settings").select("id, store_name").eq("user_id", user.id).order("created_at", { ascending: false });
      setStores(data ?? []);
      if (data?.[0]) {
        setStoreId(data[0].id);
        await loadState(data[0].id);
      } else setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync step 1 form from profile
  useEffect(() => {
    const p = state?.profile;
    if (!p) return;
    const [main, extra] = (p.business_idea ?? "").split("\nPrécisions : ");
    setIdea(main ?? "");
    setAudience(p.target_audience ?? "");
    setBudget(p.budget_range ?? "À définir");
    setLevel(p.experience_level ?? "débutant");
    setTone(p.tone ?? "premium");
    if (extra) setAnswers([extra, ""]);
  }, [state?.profile]);

  // Sync step 2 form from the pending brief
  useEffect(() => {
    const ap = state?.brief_approval;
    if (!ap || ap.status !== "pending" || ap.id === briefLoadedId) return;
    const b = (ap.preview?.after && typeof ap.preview.after === "object" ? ap.preview.after : {}) as Brief;
    setBriefPos(b.positioning ?? "");
    setBriefAud(b.target_audience ?? "");
    setBriefMsgs((b.key_messages ?? []).join("\n"));
    setBriefActs((b.next_actions ?? []).join("\n"));
    setBriefEdited(false);
    setBriefLoadedId(ap.id);
  }, [state?.brief_approval, briefLoadedId]);

  const done = [
    !!state?.profile?.business_idea,
    !!state?.profile?.approved_at,
    (state?.drafts?.length ?? 0) > 0,
    (state?.checks?.length ?? 0) > 0 && (state?.checks ?? []).every((c) => c.ok),
    !!state?.store?.is_production,
  ];
  const activeStep = done.findIndex((d) => !d);

  const run = async (key: string, fn: () => Promise<unknown>, okMsg?: string) => {
    if (!storeId) return;
    setBusy(key);
    try {
      await fn();
      if (okMsg) toast({ title: okMsg });
      await loadState(storeId);
    } catch (e) {
      toast({ title: "Échec", description: (e as Error).message, variant: "destructive" });
    } finally { setBusy(null); }
  };

  const saveProfile = async (extra?: Record<string, string>) => {
    if (!idea.trim() || idea.trim().length < 5 || !audience.trim()) {
      toast({ title: "Champs manquants", description: "Décrivez votre business et votre audience.", variant: "destructive" });
      return;
    }
    await run("profile", async () => {
      const r = await invoke<{ questions?: string[] }>("launch-agent", {
        op: "profile", store_id: storeId,
        business_idea: idea.trim(), target_audience: audience.trim(),
        budget_range: budget, experience_level: level, tone,
        ...(extra ? { extra } : {}),
      });
      setQuestions(r.questions ?? []);
      setAnswers([r.questions?.[0] ?? "", r.questions?.[1] ?? ""]);
    }, "Informations enregistrées");
  };

  const decide = async (a: Approval, op: "approve" | "reject", revise?: { args_hash: string }) => {
    await run(`ap-${a.id}`, async () => {
      if (op === "approve") {
        await invoke("ai-approvals", { op: "approve", id: a.id, args_hash: revise?.args_hash ?? a.args_hash });
        toast({ title: "Action approuvée et appliquée" });
      } else {
        await invoke("ai-approvals", { op: "reject", id: a.id });
        toast({ title: "Proposition rejetée" });
      }
    });
  };

  const reviseBrief = async () => {
    const ap = state?.brief_approval;
    if (!ap) return;
    await run("revise", async () => {
      const r = await invoke<{ args_hash: string }>("ai-approvals", {
        op: "revise", id: ap.id,
        revision: {
          positioning: briefPos.trim(),
          target_audience: briefAud.trim(),
          key_messages: briefMsgs.split("\n").map((s) => s.trim()).filter(Boolean).slice(0, 5),
          next_actions: briefActs.split("\n").map((s) => s.trim()).filter(Boolean).slice(0, 5),
        },
      });
      setBriefEdited(false);
      await invoke("ai-approvals", { op: "approve", id: ap.id, args_hash: r.args_hash });
      toast({ title: "Brief validé" });
    });
  };

  const cur = CUR[(state?.store?.default_currency ?? "").toLowerCase()] ?? (state?.store?.default_currency || "").toUpperCase();
  const stepsDone = done;
  const canLaunch = stepsDone[1] && stepsDone[3];

  const stepHeader = (i: number) => (
    <div className={`flex items-center gap-3 ${i <= activeStep || stepsDone[i] ? "cursor-pointer" : "cursor-not-allowed opacity-60"}`}
      onClick={() => { if (i <= activeStep || stepsDone[i]) setOpenStep(i); }}>
      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
        stepsDone[i] ? "bg-primary text-primary-foreground" : i === activeStep ? "border-2 border-primary text-primary" : "bg-muted text-muted-foreground"}`}>
        {stepsDone[i] ? <Check className="h-4 w-4" /> : i + 1}
      </span>
      <CardTitle className="text-base">{STEP_TITLES[i]}</CardTitle>
    </div>
  );

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <CardTitle className="text-lg flex items-center gap-2"><Rocket className="h-5 w-5 text-primary" />Lancement IA</CardTitle>
          {stores.length > 0 && (
            <Select value={storeId} onValueChange={(v) => { setStoreId(v); setState(null); setLoading(true); setOpenStep(0); loadState(v); }}>
              <SelectTrigger className="sm:w-64"><SelectValue placeholder="Boutique" /></SelectTrigger>
              <SelectContent>{stores.map((s) => <SelectItem key={s.id} value={s.id}><span translate="no">{s.store_name || "Boutique"}</span></SelectItem>)}</SelectContent>
            </Select>
          )}
        </CardHeader>
        {stores.length === 0 ? (
          <CardContent><p className="text-sm text-muted-foreground">Créez d'abord une boutique pour utiliser le Launch Agent.</p></CardContent>
        ) : state?.store?.is_production ? (
          <CardContent>
            <Badge className="gap-1"><Check className="h-3 w-3" />Boutique en ligne</Badge>
            <p className="mt-3 text-sm text-muted-foreground">Votre boutique est publiée. Retournez au tableau de bord pour la gérer au quotidien.</p>
          </CardContent>
        ) : null}
      </Card>

      {stores.length > 0 && !state?.store?.is_production && (
        <>
          {/* Étape 1 — Business */}
          <Card className={openStep === 0 ? "" : "opacity-80"}>
            <CardHeader>{stepHeader(0)}</CardHeader>
            {openStep === 0 && (
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Que voulez-vous vendre ?</label>
                  <Textarea translate="no" value={idea} onChange={(e) => setIdea(e.target.value)} rows={3} maxLength={600} placeholder="Ex. : vendre des accessoires de sport écologiques à des coureurs urbains." />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">À qui ?</label>
                  <Input translate="no" value={audience} onChange={(e) => setAudience(e.target.value)} maxLength={300} placeholder="Votre audience cible" />
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">Budget de départ</label>
                    <Select value={budget} onValueChange={setBudget}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{BUDGETS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">Expérience e-commerce</label>
                    <Select value={level} onValueChange={setLevel}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{LEVELS.map((l) => <SelectItem key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">Ton</label>
                    <Select value={tone} onValueChange={setTone}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{TONES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                {questions.length > 0 && (
                  <div className="space-y-3 rounded-lg border border-border p-3">
                    <p className="text-sm font-medium">Deux précisions utiles :</p>
                    {questions.map((q, i) => (
                      <div key={i} className="space-y-1.5">
                        <label className="text-sm text-muted-foreground">{q}</label>
                        <Input translate="no" value={answers[i] ?? ""} onChange={(e) => setAnswers((a) => a.map((x, j) => (j === i ? e.target.value : x)))} maxLength={500} />
                      </div>
                    ))}
                  </div>
                )}
                <Button disabled={busy === "profile"} onClick={() => saveProfile(questions.length > 0 && (answers[0]?.trim() || answers[1]?.trim()) ? { q1: answers[0] ?? "", q2: answers[1] ?? "" } : undefined)}>
                  {busy === "profile" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Enregistrer
                </Button>
              </CardContent>
            )}
          </Card>

          {/* Étape 2 — Brief */}
          <Card className={openStep === 1 ? "" : "opacity-80"}>
            <CardHeader>{stepHeader(1)}</CardHeader>
            {openStep === 1 && (
              <CardContent className="space-y-4">
                {state?.brief_approval?.status === "approved" ? (
                  <div className="space-y-3">
                    <Badge className="gap-1"><Check className="h-3 w-3" />Brief validé</Badge>
                    <pre translate="no" className="text-xs bg-muted rounded p-3 whitespace-pre-wrap break-words max-h-72 overflow-auto">
                      {JSON.stringify(state.brief_approval.preview?.after, null, 2)}
                    </pre>
                  </div>
                ) : state?.brief_approval?.status === "pending" ? (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">L'IA a préparé un brief. Modifiez librement, puis validez : sans validation, la suite reste bloquée.</p>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Positionnement</label>
                      <Textarea translate="no" value={briefPos} onChange={(e) => { setBriefPos(e.target.value); setBriefEdited(true); }} rows={2} maxLength={400} />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Audience</label>
                      <Input translate="no" value={briefAud} onChange={(e) => { setBriefAud(e.target.value); setBriefEdited(true); }} maxLength={200} />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Messages clés (un par ligne)</label>
                      <Textarea translate="no" value={briefMsgs} onChange={(e) => { setBriefMsgs(e.target.value); setBriefEdited(true); }} rows={3} />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Prochaines actions (une par ligne)</label>
                      <Textarea translate="no" value={briefActs} onChange={(e) => { setBriefActs(e.target.value); setBriefEdited(true); }} rows={3} />
                    </div>
                    <div className="flex flex-wrap gap-2 justify-end">
                      <Button variant="outline" size="sm" disabled={busy === "ap-" + state.brief_approval.id}
                        onClick={() => decide(state.brief_approval!, "reject")}><X className="h-4 w-4 mr-1" />Rejeter</Button>
                      <Button size="sm" disabled={busy === "revise"} onClick={reviseBrief}>
                        {busy === "revise" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
                        Valider le brief{briefEdited ? " (modifié)" : ""}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">L'IA prépare un brief structuré (positionnement, audience, messages clés) à partir de vos informations.</p>
                    <Button disabled={busy === "brief"} onClick={() => run("brief", async () => { await invoke("launch-agent", { op: "brief", store_id: storeId }); })}>
                      {busy === "brief" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Préparer le brief
                    </Button>
                  </div>
                )}
              </CardContent>
            )}
          </Card>

          {/* Étape 3 — Brouillons */}
          <Card className={openStep === 2 ? "" : "opacity-80"}>
            <CardHeader>{stepHeader(2)}</CardHeader>
            {openStep === 2 && (
              <CardContent className="space-y-4">
                {(state?.drafts?.length ?? 0) === 0 ? (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">Sélection déterministe dans le catalogue, puis brouillons créés en statut invisible (rien n'est publié).</p>
                    <Button disabled={busy === "prepare"} onClick={() => run("prepare", async () => { await invoke("launch-agent", { op: "prepare", store_id: storeId }); }, "Brouillons préparés")}>
                      {busy === "prepare" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Préparer les brouillons
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid gap-2 sm:grid-cols-2">
                      {state!.drafts.map((d) => (
                        <div key={d.id} className="flex items-center gap-3 rounded-lg border border-border p-2" translate="no">
                          {d.image ? <img src={d.image} alt={d.name} className="h-12 w-12 rounded-md object-cover" /> : <Circle className="h-10 w-10 text-muted-foreground" />}
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{d.name}</p>
                            <p className="text-xs text-muted-foreground truncate">{d.category} · {Number(d.price).toFixed(2)} {cur}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    {state?.brand_approval?.status === "pending" && (
                      <div className="rounded-lg border border-border p-3 space-y-2">
                        <p className="text-sm font-medium">Proposition d'identité visuelle</p>
                        {(() => {
                          const a = state.brand_approval!.preview?.after as { primary_color?: string; secondary_color?: string; slogan?: string } | undefined;
                          return (
                            <div translate="no" className="flex items-center gap-3">
                              <span className="h-6 w-6 rounded-full border" style={{ background: a?.primary_color ?? "#ccc" }} />
                              <span className="h-6 w-6 rounded-full border" style={{ background: a?.secondary_color ?? "#ccc" }} />
                              <span className="text-sm">{a?.slogan}</span>
                            </div>
                          );
                        })()}
                        <div className="flex gap-2 justify-end">
                          <Button variant="outline" size="sm" disabled={busy === "ap-" + state.brand_approval.id} onClick={() => decide(state.brand_approval!, "reject")}><X className="h-4 w-4 mr-1" />Rejeter</Button>
                          <Button size="sm" disabled={busy === "ap-" + state.brand_approval.id} onClick={() => decide(state.brand_approval!, "approve")}>
                            {busy === "ap-" + state.brand_approval.id ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}Approuver
                          </Button>
                        </div>
                      </div>
                    )}
                    {state?.brand_approval?.status === "approved" && (
                      <p className="text-sm text-muted-foreground flex items-center gap-2"><Check className="h-4 w-4 text-primary" />Identité visuelle appliquée.</p>
                    )}
                    <Button variant="outline" size="sm" disabled={busy === "prepare"} onClick={() => run("prepare", async () => { await invoke("launch-agent", { op: "prepare", store_id: storeId }); }, "Brouillons régénérés")}>
                      {busy === "prepare" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Recommencer la préparation
                    </Button>
                  </div>
                )}
              </CardContent>
            )}
          </Card>

          {/* Étape 4 — Vérifications */}
          <Card className={openStep === 3 ? "" : "opacity-80"}>
            <CardHeader>{stepHeader(3)}</CardHeader>
            {openStep === 3 && (
              <CardContent className="space-y-3">
                <ul className="space-y-2">
                  {(state?.checks ?? []).map((c) => (
                    <li key={c.key} className="flex items-start gap-2 text-sm">
                      {c.ok ? <Check className={`h-4 w-4 mt-0.5 ${c.warning ? "text-amber-500" : "text-primary"}`} /> : <X className="h-4 w-4 mt-0.5 text-destructive" />}
                      <span><span className="font-medium">{CHECK_LABELS[c.key] ?? c.key} : </span>{c.detail}</span>
                    </li>
                  ))}
                </ul>
                <Button variant="outline" size="sm" disabled={busy === "qa"} onClick={() => run("qa", async () => { await invoke("launch-agent", { op: "qa", store_id: storeId }); })}>
                  {busy === "qa" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Revérifier
                </Button>
              </CardContent>
            )}
          </Card>

          {/* Étape 5 — Lancement */}
          <Card className={openStep === 4 ? "" : "opacity-80"}>
            <CardHeader>{stepHeader(4)}</CardHeader>
            {openStep === 4 && (
              <CardContent className="space-y-3">
                {!stepsDone[1] && <p className="text-sm text-muted-foreground">Validez d'abord le brief (étape 2).</p>}
                {stepsDone[1] && !stepsDone[3] && <p className="text-sm text-muted-foreground">Complétez les vérifications (étape 4) pour activer le lancement.</p>}
                <Button size="lg" className="gap-2" disabled={!canLaunch || busy === "launch"}
                  onClick={() => run("launch", async () => { await invoke("launch-agent", { op: "launch", store_id: storeId }); }, "Boutique en ligne !")}>
                  {busy === "launch" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
                  Lancer ma boutique
                </Button>
                <p className="text-xs text-muted-foreground">Le lancement publie votre boutique telle quelle : brouillons invisibles, rien d'autre n'est modifié.</p>
              </CardContent>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
